import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ChalkboardTeacher, Users, Books, ClockCounterClockwise, ArrowRight,
  Calendar, Warning,
} from "@phosphor-icons/react";

export default function TeacherDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/teacher/dashboard");
        setData(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-10 text-sm text-muted-foreground">Chargement…</div>;
  if (!data) return null;

  const { counts, courses, today_label, active_period } = data;
  const pendingCourses = courses.filter(c => c.pending_grading);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Tableau de bord enseignant</div>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
          <ChalkboardTeacher size={32} weight="duotone" className="text-primary" />
          Mes cours
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {today_label}{active_period ? ` · ${active_period.name} en cours` : ""}
        </p>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Books} label="Cours actifs" value={counts.courses} testid="stat-courses" tint="primary" />
        <StatCard icon={ChalkboardTeacher} label="Classes distinctes" value={counts.unique_classes} testid="stat-classes" tint="secondary" />
        <StatCard icon={Users} label="Élèves suivis" value={counts.total_students} testid="stat-students" tint="accent" />
        <StatCard icon={ClockCounterClockwise} label="Notes en attente" value={counts.pending_grading} testid="stat-pending" tint={counts.pending_grading > 0 ? "warning" : "muted"} />
      </div>

      {pendingCourses.length > 0 && (
        <div className="mb-6 rounded-lg border border-accent/30 bg-accent/5 p-4 flex items-start gap-3">
          <Warning size={22} weight="fill" className="text-accent shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold">{pendingCourses.length} cours en attente de saisie</div>
            <div className="text-muted-foreground mt-0.5">
              Les notes de la période <b>{active_period?.name || "actuelle"}</b> ne sont pas complètes.
            </div>
          </div>
        </div>
      )}

      <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
        <Calendar size={20} weight="duotone" className="text-secondary" /> Mes cours
      </h2>

      {courses.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground text-sm">
          Aucun cours affecté à cette année académique.<br />
          Contactez la direction pour être rattaché à des matières.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map(c => (
            <Card key={c.course_id}
              className="p-5 card-hover cursor-pointer relative group"
              onClick={() => nav(`/app/cours/${c.course_id}`)}
              data-testid={`card-course-${c.course_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                  <Books size={20} weight="duotone" />
                </div>
                {c.pending_grading && (
                  <Badge className="bg-accent text-accent-foreground">Notes à saisir</Badge>
                )}
              </div>
              <div className="mt-3 font-heading text-base font-semibold">
                {c.subject}
                {c.subject_code && <span className="text-xs text-muted-foreground ml-2 font-mono">{c.subject_code}</span>}
              </div>
              <div className="text-sm mt-1">
                <b className="text-secondary">{c.class_name}</b>
                {c.group_name ? ` · Groupe ${c.group_name}` : ""}
                {c.class_level ? ` · ${c.class_level}` : ""}
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Users size={14} /> {c.student_count} élèves</span>
                <span>Coef {c.coefficient}</span>
                {c.weekly_hours > 0 && <span>{c.weekly_hours}h/sem.</span>}
              </div>
              <div className="mt-4 flex items-center justify-between text-xs">
                <div className="text-muted-foreground">
                  {c.grade_count}/{c.student_count} notes saisies
                </div>
                <ArrowRight size={16} className="text-muted-foreground group-hover:text-accent transition-colors" />
              </div>
              {(c.is_optional || c.is_elective) && (
                <div className="mt-2 flex gap-1">
                  {c.is_optional && <Badge variant="outline" className="text-[10px]">Optionnel</Badge>}
                  {c.is_elective && <Badge variant="outline" className="text-[10px]">Électif</Badge>}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid, tint }) {
  const styles = {
    primary: "bg-primary/10 text-primary",
    secondary: "bg-secondary/10 text-secondary",
    accent: "bg-accent/10 text-accent",
    warning: "bg-accent/15 text-accent",
    muted: "bg-muted text-muted-foreground",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-md ${styles[tint] || styles.muted} flex items-center justify-center mb-3`}>
        <Icon size={20} weight="duotone" />
      </div>
      <div className="font-heading text-3xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wide">{label}</div>
    </Card>
  );
}
